import { TOKEN_KEY } from '@/constants/auth.ts'
import { isAuthRefreshing } from '@/api/axios'
import { userStore } from '@/stores/userStore'
import { createRouter, createWebHistory } from 'vue-router'
const AppLayout = () => import('@/layouts/AppLayout.vue')
const AuthLayout = () => import('@/layouts/AuthLayout.vue')
const NewscastOverview = () => import('@/views/newscastOverview/Index.vue')
const FeatureStoryOverview = () => import('@/views/storyFeatureOverview/Index.vue')
const ControlRoom = () => import('@/views/controlRoom/Index.vue')
const StoryLibrary = () => import('@/views/storyLibrary/Index.vue')
const VideoLibrary = () => import('@/views/videoLibrary/Index.vue')
const Commercials = () => import('@/views/commercials/Index.vue')
const LiveStreaming = () => import('@/views/liveStreaming/Index.vue')
const Resources = () => import('@/views/resources/Index.vue')
const ArticleFilters = () => import('@/views/articleFilters/Index.vue')
const Avatars = () => import('../views/avatars/Index.vue')
const Graphs = () => import('../views/graphs/Index.vue')
const VideoLinks = () => import('@/views/videoLinks/Index.vue')
const Login = () => import('@/views/auth/Login.vue')
const Reset2Fa = () => import('@/views/auth/Reset2Fa.vue')
const ResetPassword = () => import('@/views/auth/ResetPassword.vue')
const SetNewPassword = () => import('@/views/auth/SetNewPassword.vue')
const AcceptInvitation = () => import('@/views/auth/AcceptInvitation.vue')
const ResetCredentialsForm = () => import('@/views/auth/ResetCredentialsForm.vue')
const NewOrganization = () => import('@/views/organization/NewOrganization.vue')
const NotFound = () => import('@/views/notFound/Index.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: AppLayout,
      meta: {
        requiresAuth: true,
      },
      redirect: { name: 'control-room' },
      children: [
        {
          name: 'control-room',
          path: '',
          component: ControlRoom,
          meta: { activeMenu: 'control-room' },
        },
        {
          name: 'newscast-overview',
          path: 'newscast/:id/',
          component: NewscastOverview,
          meta: { activeMenu: 'control-room' },
        },
        {
          name: 'produce-feature-overview',
          path: 'produce-feature/:id/',
          component: FeatureStoryOverview,
          meta: { activeMenu: 'control-room' },
        },
        {
          path: 'videos',
          component: VideoLibrary,
        },
        {
          name: 'story-library',
          path: 'stories',
          component: StoryLibrary,
        },
        {
          name: 'commercials',
          path: 'commercials',
          component: Commercials,
        },
        {
          name: 'live-streaming',
          path: 'live-streaming',
          component: LiveStreaming,
        },
        {
          path: 'resources',
          component: Resources,
        },
        {
          name: 'video-links',
          path: 'video-links',
          component: VideoLinks,
        },
        {
          name: 'article-filters',
          path: 'articles-filters',
          component: ArticleFilters,
        },
        {
          name: 'avatars',
          path: 'avatars',
          component: Avatars,
        },
        {
          name: 'graphs',
          path: 'graphs',
          component: Graphs,
          meta: { requiresAdmin: true },
        },
        {
          name: 'new-organization',
          path: 'new-organization',
          component: NewOrganization,
          meta: { requiresAdmin: true },
        },
      ],
    },
    {
      path: '/auth',
      component: AuthLayout,
      children: [
        {
          path: '',
          redirect: { name: 'login' },
        },
        {
          name: 'login',
          path: 'login',
          component: Login,
        },
        {
          name: '2fa',
          path: '2fa',
          component: Reset2Fa,
        },
        {
          name: 'reset-password',
          path: 'reset-password',
          component: ResetPassword,
        },
        {
          name: 'set-new-password',
          path: 'set-new-password',
          component: SetNewPassword,
        },
        {
          name: 'reset-credentials',
          path: 'reset-credentials',
          component: ResetCredentialsForm,
        },
        {
          name: 'accept-invitation',
          path: 'accept-invitation',
          component: AcceptInvitation,
        },
      ],
    },
    {
      name: 'not-found',
      path: '/:pathMatch(.*)*',
      component: NotFound,
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  if (from.name && to.fullPath !== from.fullPath) {
    // Immediately abort all in-flight network requests from the previous page,
    // but never abort if an auth token refresh is currently in progress
    if (!isAuthRefreshing() && typeof window !== 'undefined' && typeof window.stop === 'function') {
      window.stop()
    }
  }

  const token = localStorage.getItem(TOKEN_KEY)
  console.log('[ROUTER GUARD] Navigating to:', to.path, 'Token exists:', !!token)

  if (to.path.includes('/accept-invitation') || to.path.includes('/reset-credentials')) {
    console.log('[ROUTER GUARD] Bypassing guard for invitation/reset credentials')
    return next()
  }

  const isPublicAuthPage =
    to.path.includes('/reset-password') ||
    to.path.includes('/set-new-password') ||
    to.path.includes('/reset-credentials') ||
    to.path.includes('/2fa') ||
    to.path.includes('/login')

  if (isPublicAuthPage && !token) {
    console.log('[ROUTER GUARD] Allowing public auth page without token')
    return next()
  }

  if (to.meta.requiresAuth && !token) {
    console.log('[ROUTER GUARD] Redirecting to /auth/login because requiresAuth and no token')
    return next('/auth/login')
  }

  if (to.path.includes('/auth') && token) {
    console.log('[ROUTER GUARD] Redirecting logged in user to / from /auth page')
    return next('/')
  }

  const store = userStore()
  if (token && !store.getUserData) {
    await store.getCurrentUser()
  }

  if (to.meta.requiresAdmin && !store.isAnyAdmin) {
    console.log('[ROUTER GUARD] Redirecting to / because requiresAdmin and user is not admin')
    return next('/')
  }

  if (store.isSuperUser && to.name !== 'control-room' && to.name !== 'not-found' && !to.path.startsWith('/auth')) {
    console.log('[ROUTER GUARD] Redirecting SUPER_USER to / because only control-room is allowed')
    return next('/')
  }

  console.log('[ROUTER GUARD] Allowing navigation to:', to.path)
  next()
})

export default router
